import { apiCall, apiCall_FormData } from "@/setup/webApi";
import moment from "moment";

export function isSurveyNotifyAvailable(survey) {
  if (!survey?.isActive) return false;
  const today = moment().startOf("day");
  return today.isSameOrAfter(moment(survey.startDate).startOf("day"))
      && today.isSameOrBefore(moment(survey.endDate).startOf("day"));
}

export const getAll = () => {
  return new Promise((resolve, reject) => {
    apiCall("get", `SurveyForm/all`)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const getAvailable = () => {
  return new Promise((resolve, reject) => {
    apiCall("get", `SurveyForm/available`)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const getSurvey = (id) => {
  return new Promise((resolve, reject) => {
    apiCall("get", `SurveyForm/${id}`)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const createSurvey = (payload) => {
  return new Promise((resolve, reject) => {
    apiCall("post", `SurveyForm`, payload)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const updateSurvey = (id, payload) => {
  return new Promise((resolve, reject) => {
    apiCall("put", `SurveyForm/${id}`, payload)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const updateSurveyFilters = (id, filters) => {
  return new Promise((resolve, reject) => {
    apiCall("put", `SurveyForm/${id}/filters`, { filters })
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const updateSurveyExclusions = (id, payload) => {
  return new Promise((resolve, reject) => {
    apiCall("put", `SurveyForm/${id}/exclusions`, payload)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const deleteSurvey = (id) => {
  return new Promise((resolve, reject) => {
    apiCall("delete", `SurveyForm/${id}`)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const getById = (id) => {
  return new Promise((resolve, reject) => {
    apiCall("get", `SurveyForm/active/${id}`)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const addDetail = (id, answers) => {
  const formData = { answers };

  return new Promise((resolve, reject) => {
    apiCall("post", `SurveyForm/detail/${id}`, formData)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const addFiles = (id, files) => {
  const formData = new FormData();

  files.forEach((file, index) => {
    formData.append(`FileUploads[${index}].QuestionID`, file.questionID);
    file.files.forEach((fl) => {
      formData.append(`FileUploads[${index}].Files`, fl);
    });
  });

  return new Promise((resolve, reject) => {
    apiCall_FormData("post", `SurveyForm/detail/${id}/upload`, formData)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const getReport = (id) => {
  return new Promise((resolve, reject) => {
    apiCall("get", `SurveyForm/${id}/report`)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const exportReport = (id) => {
  return new Promise((resolve, reject) => {
    apiCall("get", `SurveyForm/${id}/report/export`, null, true)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const getSurveyNotifyPreview = (id) => {
  return new Promise((resolve, reject) => {
    apiCall("get", `SurveyForm/${id}/notify`)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const sendSurveyNotify = (id) => {
  return new Promise((resolve, reject) => {
    apiCall("post", `SurveyForm/${id}/notify/send`)
      .then((response) => resolve(response))
      .catch((ex) => reject(ex));
  });
};

export const FILTER_FIELDS = [
  { title: "Organization", value: "OrganizationId" },
  { title: "Section", value: "SectionId" },
  { title: "Rank", value: "RankId" },
  { title: "Status", value: "StatusId" },
  { title: "Role", value: "RoleId" },
  { title: "Gender", value: "Gender" },
  { title: "Age", value: "Age" },
  { title: "Is BGI", value: "IsBGI" },
  { title: "Is Fee Paid", value: "IsFeePaid" },
];

export const FILTER_OPERATORS = ["=", "!=", ">", "<", ">=", "<="];

export const QUESTION_TYPES = [
  "Textbox",
  "Number",
  "Select",
  "MultiSelect",
  "Checkbox",
  "DatePicker",
  "RadioGroup",
  "FileUpload",
];

export const FILE_UPLOAD_PRESETS = [
  { title: "Any file", value: "any" },
  { title: "Images only", value: "images" },
  { title: "PDF only", value: "pdf" },
  { title: "Custom", value: "custom" },
];

const IMAGE_ACCEPT = "image/*";
const PDF_ACCEPT = ".pdf";

/** Normalize a custom accept string to ".ext,.ext2" form. */
export function normalizeFileAccept(value) {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  if (trimmed === IMAGE_ACCEPT || trimmed === PDF_ACCEPT) return trimmed;

  const parts = trimmed
    .split(",")
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean)
    .map((p) => (p.startsWith(".") || p.includes("/") ? p : `.${p}`));

  return parts.length ? parts.join(",") : null;
}

/** Map stored Options value to a builder preset key. */
export function fileAcceptToPreset(options) {
  const normalized = normalizeFileAccept(options);
  if (!normalized) return "any";
  if (normalized === IMAGE_ACCEPT) return "images";
  if (normalized === PDF_ACCEPT) return "pdf";
  return "custom";
}

/** Map builder preset (+ optional custom text) to Options value for save. */
export function presetToFileAccept(preset, customValue) {
  if (preset === "images") return IMAGE_ACCEPT;
  if (preset === "pdf") return PDF_ACCEPT;
  if (preset === "custom") return normalizeFileAccept(customValue);
  return null;
}

/** HTML accept attribute value from stored Options (null = unrestricted). */
export function getFileAcceptAttr(options) {
  return normalizeFileAccept(options);
}

/** Short hint shown under the file input (type + size). */
export function getFileAcceptHint(options, maxFileSizeMb) {
  const parts = [];
  const accept = normalizeFileAccept(options);
  if (accept === IMAGE_ACCEPT) parts.push("Images only");
  else if (accept === PDF_ACCEPT) parts.push("PDF only");
  else if (accept) parts.push(`Allowed: ${accept}`);

  const maxMb = normalizeMaxFileSizeMb(maxFileSizeMb);
  if (maxMb) parts.push(`Max ${maxMb} MB`);

  return parts.join(" · ");
}

function fileExtension(fileName) {
  if (!fileName) return "";
  const i = fileName.lastIndexOf(".");
  return i >= 0 ? fileName.slice(i).toLowerCase() : "";
}

/** Whether a File matches the stored Options restriction. Empty options = any file. */
export function isFileAllowed(file, options) {
  const accept = normalizeFileAccept(options);
  if (!accept) return true;
  if (!file) return false;

  if (accept === IMAGE_ACCEPT) {
    return Boolean(file.type?.startsWith("image/"));
  }

  const allowed = accept.split(",").map((p) => p.trim().toLowerCase());
  const ext = fileExtension(file.name);
  const mime = (file.type || "").toLowerCase();

  return allowed.some((rule) => {
    if (rule === IMAGE_ACCEPT) return mime.startsWith("image/");
    if (rule.includes("/")) return mime === rule || (rule.endsWith("/*") && mime.startsWith(rule.slice(0, -1)));
    return ext === rule;
  });
}

/** Normalize max size to a positive integer MB, or null if unrestricted. */
export function normalizeMaxFileSizeMb(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.floor(n);
}

export function isFileSizeAllowed(file, maxFileSizeMb) {
  const maxMb = normalizeMaxFileSizeMb(maxFileSizeMb);
  if (!maxMb) return true;
  if (!file) return false;
  return file.size <= maxMb * 1024 * 1024;
}

/** Validate a FileList/array against Options and max size; returns error message or null. */
export function validateFilesAgainstAccept(fileList, options, maxFileSizeMb) {
  const files = Array.from(fileList || []);
  const accept = normalizeFileAccept(options);
  const maxMb = normalizeMaxFileSizeMb(maxFileSizeMb);

  for (const file of files) {
    if (accept && !isFileAllowed(file, accept)) {
      return `File "${file.name}" is not allowed. ${getFileAcceptHint(accept, maxMb)}`;
    }
    if (!isFileSizeAllowed(file, maxMb)) {
      return `File "${file.name}" exceeds the maximum size of ${maxMb} MB.`;
    }
  }
  return null;
}
